import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { clientsAPI, usersAPI } from '@shared/apiClient';
import { useDispatchWorkspace } from './DispatchContext';
import { useDragAssign } from '../../hooks/useDragAssign';
import { buildTechColorMap } from './constants';
import AssignmentCard from './components/AssignmentCard';

export default function BoardView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [dragOverTechId, setDragOverTechId] = useState(null);
  const {
    draggedItem,
    draggedAssignment,
    setDraggedAssignment,
    clearDrag,
    setSelectedDetail,
  } = useDispatchWorkspace();
  const { assignBacklogToTech, reassignAssignment } = useDragAssign();

  const { data: techniciansData } = useQuery(
    'technicians',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 })
  );

  const { data: weeklyAssignmentsData } = useQuery(
    'weeklyAssignments',
    () => clientsAPI.getWeeklyAssignments(),
    { refetchInterval: 30000 }
  );

  const technicians = useMemo(
    () => (techniciansData?.data?.users || []).filter((t) => t.isActive),
    [techniciansData]
  );

  const techColorMap = useMemo(() => buildTechColorMap(technicians), [technicians]);

  const assignmentsByTechnician = useMemo(() => {
    const assignments = weeklyAssignmentsData?.data?.assignments || [];
    const grouped = {};
    assignments.forEach((assignment) => {
      const techId = assignment.technicianId;
      if (!grouped[techId]) grouped[techId] = [];
      grouped[techId].push(assignment);
    });
    return grouped;
  }, [weeklyAssignmentsData]);

  const filteredTechnicians = useMemo(() => {
    if (!searchQuery.trim()) return technicians;
    const q = searchQuery.toLowerCase();
    return technicians.filter((tech) => {
      const name = `${tech.firstName} ${tech.lastName}`.toLowerCase();
      return name.includes(q) || (tech.email || '').toLowerCase().includes(q);
    });
  }, [technicians, searchQuery]);

  const handleDrop = async (e, technicianId) => {
    e.preventDefault();
    setDragOverTechId(null);

    if (draggedAssignment) {
      const assignment = draggedAssignment;
      clearDrag();
      if (assignment.technicianId === technicianId) return;
      reassignAssignment.mutate({ assignment, technicianId });
      return;
    }

    if (draggedItem) {
      const item = draggedItem;
      clearDrag();
      assignBacklogToTech.mutate({ item, technicianId });
    }
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-shrink-0 px-3 py-2 border-b border-surface-border bg-surface flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter technicians..."
            className="input pl-8 py-1 text-xs"
          />
        </div>
        <span className="text-2xs text-gray-500">{filteredTechnicians.length} techs</span>
      </div>

      <div className="flex-1 overflow-auto p-3">
        <div className="flex gap-3 min-h-full">
          {filteredTechnicians.map((tech) => {
            const assignments = assignmentsByTechnician[tech.id] || [];
            const color = techColorMap[tech.id];
            const isDropTarget = dragOverTechId === tech.id;

            return (
              <div
                key={tech.id}
                className={clsx(
                  'flex-shrink-0 w-56 flex flex-col rounded-md border bg-surface min-h-[400px]',
                  isDropTarget ? 'hub-drop-target border-primary-400' : 'border-surface-border'
                )}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverTechId(tech.id);
                }}
                onDragLeave={() => setDragOverTechId(null)}
                onDrop={(e) => handleDrop(e, tech.id)}
              >
                <div
                  className="flex-shrink-0 px-3 py-2 border-b border-surface-border"
                  style={{ borderTopWidth: 3, borderTopColor: color, borderTopStyle: 'solid' }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-900 truncate">
                        {tech.firstName} {tech.lastName}
                      </div>
                    </div>
                    <span className="text-2xs font-semibold tabular-nums bg-surface-secondary px-1.5 py-0.5 rounded">
                      {assignments.length}
                    </span>
                  </div>
                </div>
                <div className="flex-1 overflow-auto p-2 space-y-1">
                  {assignments.length === 0 ? (
                    <div className="text-2xs text-gray-400 text-center py-8">Drop here to assign</div>
                  ) : (
                    assignments.map((assignment) => (
                      <AssignmentCard
                        key={assignment.id}
                        assignment={assignment}
                        compact
                        isDragging={draggedAssignment?.id === assignment.id}
                        onDragStart={setDraggedAssignment}
                        onDragEnd={clearDrag}
                        onClick={(a) => setSelectedDetail({ type: 'assignment', assignment: a })}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
